import React from "react";
import ConnectedThemeProvider from "@configuratorware/configurator-frontendgui/App/Screens/Configurator/ThemeProvider";
import { merge } from "lodash";
import { createTheme } from "@material-ui/core/styles";

declare module "@material-ui/core/styles/createTheme" {
  interface Theme {
    custom?: {
      palette: {
        veryDark: string;
      };
    };
    creator: {
      productPart: {
        itemSize: string | number;
        selectedItemSize: string | number;
      };
    };
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    custom?: {
      palette: {
        veryDark: string;
      };
    };
    creator?: {
      productPart?: {
        itemSize?: string | number;
        selectedItemSize?: string | number;
      };
    };
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#98452c",
      light: "#e6e0d9",
    },
    secondary: {
      main: "#585859"
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 16,
  },

  typography: {
    fontFamily: "Poppins, Roboto, Helvetica, Arial, sans-serif",
  },

  creator: {
    productPart: {
      itemSize: "105px !important",
      selectedItemSize: "105px !important",
    },
  },

  custom: {
    palette: {
      veryDark: "#222623",
    },
  },
});

const overrideTheme = (theme) => {
  merge(theme.overrides, {
    MuiBackdrop: {
      root: {
        backgroundColor: "#fffc",
      },
    },
    MuiButton: {
      root: {
        borderRadius: 4,
        boxShadow: "none",
        textTransform: "initial",
        fontWeight: 600,
        padding: "12px 24px",
      },
      outlined: {
        padding: "12px 24px",
        boxSizing: "border-box",
        // border: "none",
        // boxShadow: `inset 0 0 0 1px ${theme.palette.secondary.main}`
      },
      outlinedSecondary: {
        border: "none",
        boxShadow: `inset 0 0 0 1px ${theme.palette.secondary.light}`,
        "&:hover": {
          border: "none",
          boxShadow: `inset 0 0 0 1px ${theme.palette.secondary.main}`,
        },
      },
      contained: {
        boxShadow: "none",
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        "&.Mui-disabled": {
          opacity: 0.4,
          backgroundColor: theme.palette.primary.main,
          color: "#fff",
        },
        "&:hover": {
          boxShadow: "none",
          backgroundColor: theme.palette.primary.dark,
        },
      },
    },
    MuiDialog: {
      paper: {
        boxShadow: "none",
        border: "1px solid #e6e0d9",
      },
    },
    MuiDialogActions: {
      root: {
        padding: "16px 24px 24px",
      },
    },
    MuiDialogTitle: {
      root: {
        fontSize: "1.75rem",
        fontWeight: 600,
        "& > h2": {
          fontSize: "1.75rem",
          fontWeight: 600,
          color: theme.custom.palette.veryDark,
        },
      },
    },
    MainButton: {
      root: {
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
        padding: "12px 24px",
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
        },
      },
    },
    OptionRuleOptionlistitem: {
      root: {
        width: "100%",
        minHeight: "auto",
      },
    },
    AmountPrice: {
      root: {
        [theme.breakpoints.down("sm")]: {
          zIndex: 10,
        },
      },
      card: {
        borderTop: `1px solid ${theme.palette.primary.light}`,
        border: `1px solid ${theme.palette.primary.light}`,
        boxShadow: "none",
        "&:last-child": {
          "@media (max-width: 599.95px)": {
            padding: 16,
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.primary.light}`,
          },
        },
      },
      dialogWrapper: {
        borderTop: `1px solid ${theme.palette.primary.light}`,
      },
      AmountPriceTypography: {
        fontSize: "1.125rem",
      },
      AmountPriceButton: {
        fontSize: "1rem",
        fontWeight: 400,
      },
      AmountPriceTypographyVat: {
        fontSize: 12,
        fontStyle: "normal",
        "& > i": {
          fontStyle: "inherit",
        },
      },
    },
    CallToAction: {
      popper: {
        top: '-20px !important',
      },
      container: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        maxWidth: '16rem',
        borderRadius: '.6rem',
        backgroundColor: '#df633b',
        left: 75,
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 9,
          height: 0,
          border: '1.25em  solid transparent',
          borderTopColor: '#df633b',
          borderBottom: 0,
          borderLeft: 0,
          marginLeft: '-0.65rem',
          marginBottom: '-1.5em',
          left: 60,
          bottom: 8,
          clipPath: 'polygon(0% 0%, 0% 100%, 100% 0%)',
          transform: 'rotate(-2deg)',
          borderRadius: '0% 0% 0% 10%'
        }
      },
      animation: {
        display: 'none',
      },
      hintText: {
        textShadow: 'none',
        fontSize: '2rem',
        color: '#FFF8FF',
        padding: '1rem 1.5rem 1rem 1.5rem',
        [theme.breakpoints.down('sm')]: {
          fontSize: '1.2rem',
        }
      },
    },
    CustomOptionsList: {
      contentStyle: {
        "& > div": {
          display: "flex",
          flexDirection: "column",
        },
      },
      dialogStyle: {
        "@media (min-width: 960px)": {
          marginLeft: 0, // Magic
        },
      },
    },
    CustomDialog: {
      MuiDialogBorder: {
        borderTop: `1px solid ${theme.palette.primary.light}`,
        border: `1px solid ${theme.palette.primary.light}`,
        boxShadow: "none",
        borderRadius: `${theme.shape.borderRadius}px !important`,
        boxSizing: "border-box",
        margin: 16,
        width: "calc(100% - 32px)",
        maxHeight: "calc(100% - 32px)",
        minHeight: 200,
      },
    },
    Header: {
      headerRoot: {
        backgroundColor: "#f9f9f9",
        boxShadow: "none",
      },
    },
    Option: {
      text: {
        "& $title": {
          fontWeight: 400,
          marginBottom: "undefined",
        },
      },
      root: {
        width: "initial",
        padding: 16,
      },
      title: {
        fontSize: "1.125rem",
      },
      price: {
        fontSize: "1.125rem",
        fontWeight: 600,
      },
    },
    Portal: {
      fullscreenPortal: {
        zIndex: 999,
      },
      fullScreenTitle: {
        [theme.breakpoints.down("sm")]: {
          borderTop: "none",
        },
      },
    },
    PriceOverview: {
      cardPriceOverview: {
        borderRadius: 0,
      },
    },
    ProductPart: {
      root: {
        width: 105,
        padding: "16px 0",
        '&.horizontal': {
          padding: 0,
        }
      },
      icon: {
        //backgroundSize: "43px", // magic...
        maxHeight: '100%',
        '&.horizontal': {
          width: 80,
          height: 80,
        },
      },
      rootTypography: {
        maxWidth: "initial",
        color: theme.palette.primary.light,
        padding: '16px 0 0 0',
        fontWeight: 600,
        fontSize: "0.825rem",
      },
      marker: {
        display: 'none',
        "&.selected": {
          backgroundColor: theme.palette.secondary.main,
          "& + $content": {
            "& $rootTypography": {
              color: theme.custom.palette.veryDark,
            },
          },
        },
      },
      content: {
        "&.vertical": {
          maxHeight: 150,
        },
        '&.horizontal': {
          width: 'auto',
        },
      },
    },
    ProductPartsList: {
      root: {
        "& .carousel-nav": {
          transform: 'scale(1)',
          opacity: "1 !important",
          color: "#c4c4c4",
          transition: `color ${theme.transitions.duration.standard}ms`,
          border: `1px solid ${theme.palette.primary.light}`,
          borderRadius: 2,
          width: 38,
          height: 38,
          lineHeight: "38px",
          fontSize: 24,
          "&.prev": {
            "&:before": {
              content: '"chevron_left"',
              fontFamily: 'Material Icons',
            },
          },
          "&.next": {
            "&:before": {
              content: '"chevron_right"',
              fontFamily: 'Material Icons',
            },
          },
          "&.active": {
            color: theme.palette.primary.main,
          },
        },
        "&.vertical": {
          paddingTop: 140, // magic
          [theme.breakpoints.down("md")]: {
            paddingTop: 140, // magic
          },
          "& .carousel-nav": {
            left: "48px !important", // not cool
          },
        },
      },
    },
    SaveConfiguration: {
      dialogContentRoot: {
        padding: "0px 24px",
      },
      TypographyCaption: {
        fontSize: "1rem",
      },
    },
    LoadConfiguration: {
      dialogContentRoot: {
        padding: "0px 24px",
      },
      TypographyCaption: {
        fontSize: "1rem",
      },
    },
    SendEmail: {
      dialogContentRoot: {
        padding: "0px 24px 24px",
      },
      TypographyCaption: {
        fontSize: "1rem",
      },
    },
    PdfDownload: {
      dialogContentRoot: {
        padding: "0px 24px 24px",
      },
      TypographyCaption: {
        fontSize: "1rem",
      },
      activeButton: {
        backgroundColor: "undefined", // Need to add an ivalid value to apply the previous valid one,
        color: "undefuned", // Need to add an ivalid value to apply the previous valid one,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
        },
      },
    },
    DefaultLayout: {
      productsList: {
        "&.vertical": {
          width: 180, // magic
        },
        "&.horizontal": {
          [theme.breakpoints.down("xs")]: {
            bottom: 180, // magic
          },
        },
      },
      outerContainer: {
        marginLeft: 20,
      },
      visualization: {
        '&.withBottomList': {
          [theme.breakpoints.down('sm')]: {
            height: 'calc(100% - 320px)',
          },
          '@media (min-aspect-ratio: 13 / 9) and (min-height: 280px) and (max-width: 825px)': {
            height: '100%',
          },
        },
      },
    },
    CreatorAlignTarget: {
      root: {
        [theme.breakpoints.down('sm')]: {
          maxHeight: 'calc(100% - 320px)',
          marginTop: 0,
        },
        '@media (min-aspect-ratio: 13 / 9) and (min-height: 280px) and (max-width: 825px)': {
          maxHeight: '100%',
        },
      },
    },
    VariantChooser: {
      typographyBody: {
        color: theme.palette.primary.main,
        fontSize: 18, // magic
      },
    },
    ToolbarList: {
      root: {
        '& > span:nth-child(4)': {
          display: 'none'
        },
      },
    },
    FullScreenView: {
      buttonView: {
        display: 'none',
      },
      button: {
        display: 'none',
      },
    },
  });
};

export default ({ children }) => (
  <ConnectedThemeProvider customTheme={theme} withTheme={overrideTheme}>
    {children}
  </ConnectedThemeProvider>
);
